import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import WarehousesApi from '../../../../services/api/WarehousesApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import ButtonComponent from '../../../../components/common/forms/Button/ButtonComponent';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import styled from 'styled-components';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import { API_URL, ROLES } from '~/src/utils/constants';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';
import AddressInputComponent from '~/src/components/common/AddressInputForm';
import RolesDependent from '~/src/components/common/RolesDependent';
import AutocompletePaginatedComponent from '~/src/components/common/forms/Autcomplete/AutocomplitePaginatedComponent';

export default function WarehouseEdit(): JSX.Element {
  const { id } = useParams();
  const [itemData, setItemData] = useState({
    name: '',
    postCode: '',
    street: '',
    buildNumber: '',
    countryId: '',
    cityId: '',
    svilaClientId: '',
    address: null,
    dataArrived: false,
  });
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);

    const warehouseDto = {
      name: values.name,
      svilaClientId: values.svilaClientId,
      address: {
        postCode: values.postCode,
        street: values.street,
        building: values.buildNumber,
        state: values.state,
        countryId: values.countryId,
        cityId: values.cityId,
      }
    }

    let result = null;
    if (id) {
      result = await WarehousesApi.update(id, warehouseDto);
    } else {
      result = await WarehousesApi.create(warehouseDto);
    }
    if (result.status === StatusCodes.OK) {
      history.push('/warehouses');
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async () => {
    const result = await WarehousesApi.get(id);
    const newItemData = { ...result.data, dataArrived: true };
    setItemData(newItemData);
  };

  const onDelete = async () => {
    const result = await WarehousesApi.delete(id);
    if (result.status === StatusCodes.OK) {
      history.push('/warehouses');
    }
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  return (
    <>
      <WhitePad>
        <TitleComponent
          onLeftHandler={history.goBack}
          titleLeft="Back"
          title={`${id ? 'Edit ' : 'Create '} Warehouse`}
        />
        <FormWrapper style={{ display: 'flex', alignItems: 'center' }}>
          {id && itemData.dataArrived === false ? null : (
            <Formik
              initialValues={{
                name: itemData.name,
                postCode: itemData.address?.postCode || '',
                street: itemData.address?.street || '',
                buildNumber: itemData.address?.building || '',
                state: itemData.address?.state || '',
                countryId: itemData.address?.country.id || '',
                cityId: itemData.address?.city.id || '',
                svilaClientId: itemData.svilaClientId || '',
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                return saveItem(values, setSubmitting);
              }}
            >
              {({ values, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting, errors }) => (
                <form onSubmit={handleSubmit} autoComplete="off">
                  <FormRow>
                    <TextInputComponent
                      name="name"
                      label="Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  </FormRow>
                  <AddressInputComponent
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                  />
                  <RolesDependent
                    roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}>
                    <FormRow>
                      <AutocompletePaginatedComponent
                        url={`${API_URL}/svila-clients/get-all`}
                        name="svilaClientId"
                        initialValue={values.svilaClientId}
                        onChange={(name, value) => {
                          setFieldValue(name, value);
                        }}
                        getOptionLabel={(option) => `${option.name}`}
                        errorMessage={errors.svilaClientId}
                        cols={12}
                        label="Svila Client"
                      />
                    </FormRow>
                  </RolesDependent>
                  <ActionWrapper>
                    <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Save" />
                    {id && (
                      <BorderlessButton marginLeft="30px" type="button" onClick={onDelete} title="Delete" />
                    )}
                  </ActionWrapper>
                </form>
              )}
            </Formik>
          )}
        </FormWrapper>
      </WhitePad>
    </>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`;